import type { Permission } from "@oms/types";
import type { LucideIcon } from "lucide-react";
import {
  Building2,
  CreditCard,
  History,
  Plug,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from "lucide-react";

export const SETTINGS_SECTIONS = [
  "workspace",
  "users",
  "roles",
  "activity",
  "integrations",
  "billing",
  "preferences",
] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

export interface SettingsNavItem {
  section: SettingsSection;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Hidden unless the member holds this permission. */
  permission?: Permission;
  superAdminOnly?: boolean;
}

export interface SettingsNavGroup {
  label: string;
  items: SettingsNavItem[];
}

/**
 * The settings sub-navigation. Everything here is permission-gated, so two
 * members of the same workspace can see very different settings.
 */
export const SETTINGS_NAV: SettingsNavGroup[] = [
  {
    label: "General",
    items: [
      {
        section: "workspace",
        label: "Workspace",
        description: "Organization profile and identity",
        icon: Building2,
        permission: "settings.view",
      },
      {
        section: "preferences",
        label: "Preferences",
        description: "Defaults for this workspace",
        icon: SlidersHorizontal,
        permission: "settings.view",
      },
    ],
  },
  {
    label: "Access control",
    items: [
      {
        section: "users",
        label: "Users",
        description: "Team members and their roles",
        icon: Users,
        permission: "members.view",
      },
      {
        section: "roles",
        label: "Roles & permissions",
        description: "What each role can see and do",
        icon: ShieldCheck,
        permission: "roles.view",
      },
      {
        section: "activity",
        label: "Activity",
        description: "Audit trail of workspace changes",
        icon: History,
        permission: "audit.view",
      },
    ],
  },
  {
    label: "Connections",
    items: [
      {
        section: "integrations",
        label: "Channels",
        description: "Connected storefronts and marketplaces",
        icon: Plug,
        permission: "integrations.view",
      },
    ],
  },
  {
    label: "Account",
    items: [
      {
        section: "billing",
        label: "Billing",
        description: "Plan, invoices and payment method",
        icon: CreditCard,
        permission: "billing.view",
      },
    ],
  },
];

export function isSettingsSection(value: unknown): value is SettingsSection {
  return (
    typeof value === "string" &&
    (SETTINGS_SECTIONS as readonly string[]).includes(value)
  );
}
